/**
 * Thin wrapper over the Web Notifications API.
 *
 * v1 scope: shows a notification while the tab is OPEN (even backgrounded).
 * When-the-tab-is-closed notifications require a service worker + web-push
 * subscription — follow-up. For now, a reviewer who leaves their laptop with
 * the campaign page open still gets pinged; one who fully closes the tab
 * relies on the existing email.
 *
 * All helpers are no-ops when:
 *   - window.Notification is undefined (older browsers, non-secure contexts)
 *   - permission is 'denied' (user previously said no)
 *   - the user settings toggle is off (see isEnabled below)
 */

import { brandLogos } from './branding';

const STORAGE_KEY = 'sidekick:browserNotifications:enabled';

/** True when the browser exposes the Notifications API at all. */
export function isSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** Current permission: 'default' | 'granted' | 'denied' | 'unsupported'. */
export function getPermission() {
  if (!isSupported()) return 'unsupported';
  return window.Notification.permission;
}

/** Reviewer-level toggle, persisted across sessions. Defaults to on. */
export function isEnabled() {
  if (typeof localStorage === 'undefined') return true;
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === null ? true : raw === 'true';
}

export function setEnabled(on) {
  try { localStorage.setItem(STORAGE_KEY, on ? 'true' : 'false'); }
  catch (_) { /* best-effort */ }
}

/**
 * Ask the browser for permission. Safe to call repeatedly; the browser
 * short-circuits when permission is already granted or denied. Most modern
 * browsers require a user gesture — if the call is made outside one the
 * returned promise resolves to 'default' without prompting, and our later
 * notify() calls become no-ops. That's the acceptable degradation for v1;
 * we'll add an explicit opt-in button if user feedback wants it.
 */
export async function requestPermission() {
  if (!isSupported()) return 'unsupported';
  if (window.Notification.permission !== 'default') {
    return window.Notification.permission;
  }
  try {
    return await window.Notification.requestPermission();
  } catch (err) {
    console.warn('[browserNotifications] requestPermission failed:', err);
    return 'default';
  }
}

/**
 * Fire a notification. Drops silently when any precondition fails — this
 * is designed to be called unconditionally from polling / event handlers
 * without guarding at the call site.
 *
 * @param {object} opts
 * @param {string} opts.title    — required, max ~50 chars for most OSes
 * @param {string} [opts.body]   — longer description
 * @param {string} [opts.tag]    — dedup key; a later notification with the
 *                                 same tag REPLACES the earlier one instead
 *                                 of stacking. Use the campaignId for
 *                                 campaign-lifecycle notifications.
 * @param {string} [opts.icon]   — URL; defaults to the app logo
 * @param {() => void} [opts.onClick] — fired when the reviewer clicks the
 *                                 notification. Typical: focus the tab and
 *                                 route to the relevant page.
 */
export function notify({ title, body, tag, icon, onClick }) {
  if (!isSupported()) return null;
  if (!isEnabled()) return null;
  if (window.Notification.permission !== 'granted') return null;
  if (!title) return null;

  try {
    const n = new window.Notification(title, {
      body: body || '',
      tag,
      icon: icon || brandLogos.square,
    });
    if (onClick) {
      n.onclick = (e) => {
        e.preventDefault?.();
        try { window.focus(); } catch (_) { /* */ }
        try { onClick(); } catch (err) {
          console.warn('[browserNotifications] onClick handler threw:', err);
        }
        try { n.close(); } catch (_) { /* */ }
      };
    }
    return n;
  } catch (err) {
    console.warn('[browserNotifications] construction failed:', err);
    return null;
  }
}
